/**
 * @file ChatFileHandler.ts
 * @description Validación de archivos para el chat (superficie `chat`).
 *
 * Los límites NO se declaran aquí: se derivan de `shared/uploadLimits.js`, la fuente única que
 * comparten frontend y backend (#266). Antes había que acordarse de tocar este fichero Y el
 * backend cada vez que cambiaba un valor; eso ya falló (#239).
 *
 * IMPORTANTE: La validación del frontend es para UX. El backend realiza la validación
 * autoritativa con magic bytes — y contra el MIME SNIFFEADO, no contra el que declara el
 * navegador, que es el que se ve aquí. De ahí que esta vista incluya alias
 * (hoy sólo `application/x-subrip`) que el backend no necesita.
 */

import { FileUploadOptions } from '../../../hooks/useFileUpload';
import {
  SURFACES,
  sizeByBrowserMime,
  acceptForInput,
  allowedTypesForSurface
} from '../../../../shared/uploadLimits';

/**
 * Tamaño máximo por MIME **declarado por el navegador**, para la superficie `chat`.
 * Derivado de shared/uploadLimits.js — no editar aquí: cambia la política de la superficie.
 */
export const FILE_SIZE_LIMITS: Record<string, number> = sizeByBrowserMime('chat');

/** Máximo de ficheros por mensaje. Fuente única: SURFACES.chat. */
export const MAX_FILES: number = SURFACES.chat.maxFiles;

/** Máximo agregado por mensaje, en bytes. Fuente única: SURFACES.chat. */
export const MAX_TOTAL_SIZE: number = SURFACES.chat.maxTotalSize;

/**
 * Extensión → MIME, para inferir el tipo cuando el navegador no lo declara.
 *
 * Se DERIVA de `SURFACES.chat`: esta tabla estaba escrita a mano y el 2026-09-03 se comprobó que
 * ya se había quedado atrás —faltaban formatos que el catálogo sí aceptaba—, con el efecto de que
 * `getMaxSizeForFile` caía al default de 10MB para ellos en vez de aplicar su tope real. Un mapa
 * literal aquí vuelve a desincronizarse en el siguiente cambio de catálogo.
 */
export const EXTENSION_TO_MIME: Record<string, string> = Object.entries(
  allowedTypesForSurface('chat')
).reduce((acc: Record<string, string>, [mime, cfg]) => {
  for (const ext of (cfg as { extensions: string[] }).extensions) acc[ext] = mime;
  return acc;
}, {});

/**
 * Get max file size for a given file based on its type
 * Falls back to default 10MB if type not found
 *
 * @param file File object to check
 * @returns Maximum allowed size in bytes
 */
export const getMaxSizeForFile = (file: File): number => {
  // First try to use the file's MIME type
  if (file.type && FILE_SIZE_LIMITS[file.type]) {
    return FILE_SIZE_LIMITS[file.type];
  }

  // Fallback: try to infer from extension
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension && EXTENSION_TO_MIME[extension]) {
    const inferredMime = EXTENSION_TO_MIME[extension];
    if (FILE_SIZE_LIMITS[inferredMime]) {
      return FILE_SIZE_LIMITS[inferredMime];
    }
  }

  // Default: 10MB
  return 10 * 1024 * 1024;
};

/**
 * Format file size in human-readable format
 * @param bytes Size in bytes
 * @returns Formatted string (e.g., "10.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Validate file type and provide helpful error message
 * @param file File to validate
 * @throws Error with user-friendly message if file type not supported
 */
export const validateFileType = (file: File): void => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (!extension) {
    throw new Error(`El archivo "${file.name}" no tiene extensión. Por favor, usa un archivo con extensión válida.`);
  }

  // Check if extension is in our allowed list
  const allowedExtensions = Object.keys(EXTENSION_TO_MIME);
  if (!allowedExtensions.includes(extension)) {
    // Sugerencias para lo que NO se va a aceptar nunca. Aquí no cabe nada del catálogo:
    // una entrada para un formato que sí admitimos es inalcanzable (el `if` de arriba ya
    // dejó pasar el fichero) y, si deja de serlo, miente. Así estaban `.doc` («convierte a
    // .docx») y `.html` («adjúntalo como .txt») cuando se habilitaron los dos.
    const suggestions: Record<string, string> = {
      'exe': 'Los archivos ejecutables no están permitidos por seguridad',
      'js': 'Los archivos JavaScript no están permitidos. Por favor, adjúntalos como .txt',
      'xml': 'Los archivos XML no están soportados. Por favor, adjúntalos como .txt'
    };

    const suggestion = suggestions[extension];
    if (suggestion) {
      throw new Error(`Archivo .${extension} no soportado. ${suggestion}`);
    }

    // La lista se DERIVA del catálogo: la que había escrita a mano se quedó atrás y
    // anunciaba menos formatos de los que ya aceptábamos.
    throw new Error(
      `El tipo de archivo .${extension} no está soportado. ` +
      `Tipos permitidos: ${allowedExtensions.join(', ')}`
    );
  }
};

/**
 * Validate file size against type-specific limits
 * @param file File to validate
 * @throws Error with user-friendly message if file too large
 */
export const validateFileSize = (file: File): void => {
  const maxSize = getMaxSizeForFile(file);

  if (file.size > maxSize) {
    const maxSizeFormatted = formatFileSize(maxSize);
    const fileSizeFormatted = formatFileSize(file.size);
    const extension = file.name.split('.').pop()?.toUpperCase();

    throw new Error(
      `El archivo "${file.name}" (${fileSizeFormatted}) supera el límite de ${maxSizeFormatted} para archivos .${extension}. ` +
      `Por favor, reduce el tamaño del archivo o divídelo en partes más pequeñas.`
    );
  }
};

/**
 * Validate a single file (type + size)
 * @param file File to validate
 * @throws Error with user-friendly message if validation fails
 */
export const validateFile = (file: File): void => {
  validateFileType(file);
  validateFileSize(file);
};

/**
 * Prepara los archivos para ser enviados en el chat
 * @param files Lista de archivos a preparar
 * @returns FormData con los archivos preparados
 */
export const prepareChatFiles = (files: File[]): FormData => {
  console.log('[ChatFileHandler] Preparando archivos:', files.map(f => ({ name: f.name, size: f.size })));
  const formData = new FormData();
  files.forEach(file => {
    console.log(`[ChatFileHandler] Añadiendo archivo al FormData: ${file.name} (${file.size} bytes)`);
    formData.append('files', file);
  });
  return formData;
};

// Tope de longitud del MENSAJE (caracteres). Override opcional vía
// REACT_APP_LLM_CONTEXT_WINDOW (ver env.example) — el default de 100000*0.9 = 90000
// caracteres (~23k tokens) es suficiente para texto tecleado o pegado.
//
// IMPORTANTE — por qué esto NO cuenta los adjuntos: los ficheros del chat nunca se
// inlinean en el prompt. Viajan como binario a S3 (threadService.createMessage) y el
// payload a N8N solo lleva {url, name, key} (server/services/messageService.js:835-842).
// N8N los vectoriza en la colección `Chat_{threadId}` (flujo rag_chat →
// docs/architecture/knowledge/RAG_CURRENT_STATE.md:44) y el agente los consulta vía tool T_RAG_CHAT.
// Lo único que consume ventana de contexto inline es `message`.
// Histórico (#444): la versión anterior sumaba `await file.text()` de cada adjunto text/*,
// rechazando un .txt de 140 KB perfectamente legal. Era código vestigial de un diseño
// (nunca cableado, ver el TODO de prepareChatFiles) en el que el contenido sí se inlineaba.
const MESSAGE_CHAR_LIMIT = Math.floor(
  parseInt(process.env.REACT_APP_LLM_CONTEXT_WINDOW || '100000', 10) * 0.9
);

/**
 * Valida que el mensaje no supere el tope de caracteres inline.
 *
 * NO recibe los adjuntos a propósito: no consumen ventana de contexto (van por RAG,
 * ver el bloque de arriba). Antes la firma era `(message, files)` y el segundo
 * parámetro se contaba; se ha eliminado para que sea **imposible** volver a contarlo
 * sin cambiar la firma y los dos call sites. El compilador es el candado.
 *
 * @param message Mensaje del usuario
 * @throws Error si el mensaje supera MESSAGE_CHAR_LIMIT
 */
export const validateContextWindow = async (message: string): Promise<void> => {
  if (message.length > MESSAGE_CHAR_LIMIT) {
    throw new Error(
      `Tu mensaje tiene ${message.length.toLocaleString('es-ES')} caracteres y el máximo es ` +
      `${MESSAGE_CHAR_LIMIT.toLocaleString('es-ES')}. Recorta el texto o adjúntalo como fichero ` +
      `— los adjuntos no cuentan para este límite.`
    );
  }
};

/**
 * Comprueba número de ficheros y tamaño agregado del LOTE.
 *
 * Vive aparte porque lo necesitan dos caminos: `handleChatFiles` (última barrera antes de enviar)
 * y `chatFileOptions.customBatchValidator` (que es lo que hace que ARRASTRAR y PEGAR validen —
 * esas dos vías no pasan por ChatFileUploader, ver ChatInput.handleDrop).
 *
 * Los mensajes son literalmente los de antes: esto no cambia lo que lee el usuario.
 *
 * @param files Lote completo ya combinado
 * @throws Error con mensaje legible si se pasa de conteo o de total
 */
export const validateBatchLimits = (files: File[]): void => {
  if (files.length > MAX_FILES) {
    throw new Error(
      `No puedes adjuntar más de ${MAX_FILES} archivos a la vez. ` +
      `Has seleccionado ${files.length} archivos. Por favor, reduce la cantidad.`
    );
  }

  const totalSize = files.reduce((sum, file) => file.size + sum, 0);
  if (totalSize > MAX_TOTAL_SIZE) {
    throw new Error(
      `El tamaño total de los archivos (${formatFileSize(totalSize)}) supera el límite de ${formatFileSize(MAX_TOTAL_SIZE)}. ` +
      `Por favor, reduce el número o tamaño de los archivos.`
    );
  }
};

/**
 * Handler principal para la subida de archivos en el chat
 * Valida cada archivo antes de procesarlo
 * @param files Archivos seleccionados
 * @throws Error with user-friendly message if validation fails
 */
export const handleChatFiles = async (files: File[]): Promise<void> => {
  try {
    // Conteo y total: mismo veredicto que aplican arrastrar y pegar (validateBatchLimits).
    validateBatchLimits(files);

    console.log('[ChatFileHandler] Iniciando procesamiento de archivos:', files.length);
    console.log('[ChatFileHandler] Detalles de archivos:', files.map(f => ({
      name: f.name,
      size: f.size,
      type: f.type,
      lastModified: new Date(f.lastModified).toISOString()
    })));

    // Validate each file individually
    // This will throw on first error with a user-friendly message
    for (const file of files) {
      validateFile(file);
    }

    // All validations passed
    const formData = prepareChatFiles(files);
    console.log('[ChatFileHandler] FormData preparado con éxito - Validaciones completadas');

    // TODO: Implementar la subida al backend
    // await uploadFilesToChat(formData);

  } catch (error) {
    console.error('[ChatFileHandler] Error al procesar archivos:', error);
    throw error;
  }
};

/**
 * Opciones por defecto para la subida de archivos en el chat.
 *
 * Los tipos permitidos NO se listan aquí: salen de `SURFACES.chat` en shared/uploadLimits.js.
 * Una lista a mano en este comentario se desincroniza del catálogo sin que nada avise — es lo
 * que pasó hasta #728, cuando seguía anunciando audio y ZIP después de retirarlos.
 */
export const chatFileOptions: FileUploadOptions = {
  // Tipos de archivo permitidos para el chat. Derivados de shared/uploadLimits.js (superficie
  // `chat`) — ya no hay nada que "mantener sincronizado con el backend" a mano.
  // Android (API ≤29) file picker resolves MIME types, not extensions — same fix as FileUploadDialog (#74/#92).
  accept: acceptForInput('chat'),
  // Permitimos múltiples archivos
  multiple: true,
  // NO usar maxSize global - se valida por tipo con validadores custom
  // maxSize: undefined,
  // Validador custom para tipos de archivo con mensajes útiles
  customTypeValidator: validateFileType,
  // Validador custom para tamaño por tipo de archivo
  customSizeValidator: validateFileSize,
  // Validador del LOTE: conteo y tamaño agregado.
  // Esto es lo que hace que ARRASTRAR y PEGAR apliquen los mismos topes que el botón "+":
  // esas dos vías no pasan por ChatFileUploader, llaman directamente al hook del padre
  // (ChatInput.handleDrop/handlePaste). Hasta #266 no comprobaban ni cuántos ficheros ni el
  // total, así que soltar 20 ficheros no producía ningún aviso y reventaba en el servidor.
  customBatchValidator: validateBatchLimits,
  // Manejador de errores por defecto
  onError: (error: Error) => {
    console.error('[ChatFileHandler] Error en la subida de archivos:', error);
  }
}; 